/* ============================================================
   assets/faq-page.js
   Build: no-relocation 2026-06-03j.
   FAQ page behavior: sidebar master-detail + cross-category search.

   ARCHITECTURE (no DOM relocation):
   - Shopify renders the shell section and each category section as separate
     sibling .shopify-section wrappers. We DO NOT move them. Moving section
     wrappers broke the theme editor (editing the shell destroyed the moved
     category sections and blanked the preview, and severed block-to-settings
     links). Instead we tag their common parent (data-faq-host) and lay out
     with CSS grid: the shell sits in the left column (intro + sidebar), the
     active category section in the right column. JS only shows/hides panes
     in place, builds the sidebar, runs search, and toggles answers.

   - PROGRESSIVE ENHANCEMENT. The server renders every category and every
     answer with all <details> open. This script collapses/hides things. If it
     never runs (JS off / error), the page is a fully readable long-form FAQ.
     All DOM mutation is inside a guarded init; a thrown error leaves the
     readable fallback intact.

   - NO AJAX. All content is in the DOM. JS toggles visibility/open state and
     filters during search.

   - Re-inits on shopify:section:* / shopify:block:select so it works in the
     theme editor when Kim adds/edits/reorders/hides blocks and sections.
   ============================================================ */

(function () {
  'use strict';

  // Singleton guard for one-time GLOBAL wiring (hashchange, shopify:* events).
  // Per-root idempotency is handled by the root.__faqInit flag below.
  if (window.__dionisFaqPageLoaded) return;
  window.__dionisFaqPageLoaded = true;

  var ROOT_SELECTOR = '[data-faq-root]';

  function getPanes() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-faq-category-pane]')
    );
  }

  // Hiding uses the WRAPPER (the Shopify section element), not the inner pane.
  // A hidden wrapper gets display none and leaves the CSS grid entirely, so
  // auto placement keeps the one visible category beside the sidebar with no
  // phantom rows or gaps from the other zero-height wrappers.
  function wrapperOf(pane) {
    return pane.closest('.shopify-section') || pane;
  }
  function setPaneHidden(pane, hidden) {
    var w = wrapperOf(pane);
    if (hidden) w.setAttribute('hidden', '');
    else w.removeAttribute('hidden');
  }
  function paneIsHidden(pane) {
    return wrapperOf(pane).hasAttribute('hidden');
  }

  // Restore a pane to its default answer state: items marked open-by-default
  // are open, everything else is closed. Used at init and when navigating
  // away from a category, so every return visit starts at the same baseline
  // height.
  function resetPaneToDefault(pane) {
    var items = pane.querySelectorAll('[data-faq-item]');
    Array.prototype.forEach.call(items, function (item) {
      if (item.getAttribute('data-open-default') === 'true') {
        item.setAttribute('open', '');
      } else {
        item.removeAttribute('open');
      }
    });
  }

  // ---- Pill rail edge fades (mobile) ----
  // iOS shows no resting scrollbar, so CSS masks keyed off data-fade signal
  // hidden pills instead. States: end = more pills to the right, start =
  // more to the left, both = somewhere in the middle. The attribute is
  // removed entirely when nothing overflows (desktop vertical list).
  function updateNavFade(nav) {
    if (!nav) return;
    var overflow = nav.scrollWidth - nav.clientWidth;
    if (overflow <= 1) {
      nav.removeAttribute('data-fade');
      return;
    }
    var atStart = nav.scrollLeft <= 1;
    var atEnd = nav.scrollLeft >= overflow - 1;
    nav.setAttribute('data-fade', atStart ? 'end' : atEnd ? 'start' : 'both');
  }

  function init(root) {
    if (!root || root.__faqInit) return;

    var panes = getPanes();
    if (!panes.length) return; // nothing to enhance; leave readable fallback

    var nav = root.querySelector('[data-faq-nav]');
    var navFallback = root.querySelector('[data-faq-nav-fallback]');
    var searchInput = root.querySelector('[data-faq-search-input]');
    var searchClear = root.querySelector('[data-faq-search-clear]');

    // ---- Host-parent layout (NO relocation) ----
    // Find the common parent of the shell wrapper and the category section
    // wrappers, and tag it so CSS can grid them. Nothing is moved.
    var shellWrapper = root.closest('.shopify-section') || root.parentNode;
    var host = shellWrapper ? shellWrapper.parentNode : null;
    if (!host) return;
    host.setAttribute('data-faq-host', 'true');

    // Mirror the shell spacing settings onto the host so the whole FAQ grid
    // (sidebar AND category panel) gets the same top and bottom spacing.
    var spacingAbove = root.getAttribute('data-spacing-above');
    var spacingBelow = root.getAttribute('data-spacing-below');
    if (spacingAbove) host.setAttribute('data-faq-spacing-above', spacingAbove);
    if (spacingBelow) host.setAttribute('data-faq-spacing-below', spacingBelow);

    // Search-results container lives on the host (right column during search).
    // It's a plain div we own (not a Shopify section), so creating/owning it is
    // safe and survives shell re-renders because we recreate it if missing.
    var resultsBox = host.querySelector(':scope > [data-faq-search-results]');
    if (!resultsBox) {
      resultsBox = document.createElement('div');
      resultsBox.className = 'faq-search-results';
      resultsBox.setAttribute('data-faq-search-results', '');
      resultsBox.setAttribute('hidden', '');
      resultsBox.setAttribute('aria-live', 'polite');
      host.appendChild(resultsBox);
    }

    // ---- Mark enhanced (drives CSS grid + in-place hiding) ----
    root.setAttribute('data-faq-enhanced', 'true');
    root.__faqInit = true;

    // ---- Collapse answers not explicitly open-by-default ----
    panes.forEach(function (pane) {
      resetPaneToDefault(pane);
    });

    // ---- Build sidebar nav from rendered panes ----
    var navItems = [];
    if (nav) {
      if (navFallback) navFallback.setAttribute('hidden', '');
      // Clear any stale buttons (editor re-init safety).
      Array.prototype.forEach.call(
        nav.querySelectorAll('.faq-nav__item'),
        function (b) { b.remove(); }
      );

      panes.forEach(function (pane, i) {
        var handle = pane.getAttribute('data-faq-category-pane');
        var title = pane.getAttribute('data-faq-title') || ('Category ' + (i + 1));

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'faq-nav__item';
        btn.textContent = title;
        btn.setAttribute('data-faq-nav-target', handle);
        btn.setAttribute('aria-current', 'false');
        nav.appendChild(btn);
        navItems.push(btn);
      });
    }

    // ---- Pill rail fade: set now, track scrolling, expose for refresh ----
    if (nav && !nav.__faqFadeBound) {
      nav.addEventListener('scroll', function () { updateNavFade(nav); }, { passive: true });
      nav.__faqFadeBound = true;
    }
    updateNavFade(nav);
    root.__faqNavFade = function () { updateNavFade(nav); };

    // ---- Initial active pane (first match wins) ----
    var activeIndex = 0;
    var foundActive = false;
    panes.forEach(function (pane, i) {
      if (!foundActive && pane.getAttribute('data-active-default') === 'true') {
        activeIndex = i;
        foundActive = true;
      }
    });

    function showPane(index) {
      panes.forEach(function (pane, i) {
        var hide = i !== index;
        // Leaving a visible category: restore its answers to the default
        // state so every return visit starts at the measured baseline.
        if (hide && !paneIsHidden(pane)) resetPaneToDefault(pane);
        setPaneHidden(pane, hide);
      });
      navItems.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      // On mobile the nav is a horizontal scroll row of pills; bring the
      // active pill into view. No-op when the nav has no overflow (desktop
      // vertical list) or when called from a click on an already visible pill.
      var activeBtn = navItems[index];
      if (
        activeBtn &&
        nav &&
        nav.scrollWidth > nav.clientWidth &&
        typeof activeBtn.scrollIntoView === 'function'
      ) {
        activeBtn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    function currentIndex() {
      var idx = activeIndex;
      navItems.forEach(function (btn, i) {
        if (btn.getAttribute('aria-current') === 'true') idx = i;
      });
      return idx;
    }

    // ---- Search index from rendered DOM (question + answer text) ----
    var index = [];
    panes.forEach(function (pane) {
      var category = pane.getAttribute('data-faq-title') || '';
      var items = pane.querySelectorAll('[data-faq-item]');
      Array.prototype.forEach.call(items, function (item) {
        var q = item.getAttribute('data-faq-question') || '';
        var aEl = item.querySelector('[data-faq-answer]');
        var a = aEl ? (aEl.getAttribute('data-faq-answer') || '') : '';
        index.push({ category: category, question: q, answerText: a, node: item });
      });
    });

    var searching = false;

    function exitSearch() {
      if (!searching) return;
      searching = false;
      if (resultsBox) {
        resultsBox.setAttribute('hidden', '');
        resultsBox.innerHTML = '';
      }
      showPane(currentIndex());
    }

    function runSearch(term) {
      term = term.trim().toLowerCase();

      if (!term) {
        if (searchClear) searchClear.setAttribute('hidden', '');
        searching = false;
        if (resultsBox) {
          resultsBox.setAttribute('hidden', '');
          resultsBox.innerHTML = '';
        }
        var anyVisible = panes.some(function (p) { return !paneIsHidden(p); });
        showPane(anyVisible ? currentIndex() : activeIndex);
        return;
      }

      if (searchClear) searchClear.removeAttribute('hidden');
      searching = true;

      // Hide all category panes while showing results.
      panes.forEach(function (pane) { setPaneHidden(pane, true); });
      navItems.forEach(function (btn) { btn.setAttribute('aria-current', 'false'); });

      var matches = index.filter(function (entry) {
        return (
          entry.question.toLowerCase().indexOf(term) !== -1 ||
          entry.answerText.toLowerCase().indexOf(term) !== -1
        );
      });

      if (!resultsBox) return;
      resultsBox.innerHTML = '';
      resultsBox.removeAttribute('hidden');

      var count = document.createElement('p');
      count.className = 'faq-search-results__count';
      count.textContent =
        matches.length + (matches.length === 1 ? ' result' : ' results') +
        ' for \u201c' + term + '\u201d';
      resultsBox.appendChild(count);

      if (!matches.length) {
        var empty = document.createElement('p');
        empty.className = 'faq-search-empty';
        empty.textContent = 'No questions matched. Try different words.';
        resultsBox.appendChild(empty);
        return;
      }

      matches.forEach(function (entry) {
        var wrap = document.createElement('div');
        wrap.className = 'faq-result';

        var label = document.createElement('span');
        label.className = 'faq-result__category';
        label.textContent = entry.category;
        wrap.appendChild(label);

        // Clone so the original stays intact in its pane.
        var clone = entry.node.cloneNode(true);
        clone.setAttribute('open', '');
        clone.removeAttribute('id');
        var summary = clone.querySelector('.faq-item__summary');
        if (summary) summary.removeAttribute('id');
        // Strip Shopify editor attributes from clones so the editor doesn't
        // try to bind duplicate block/section targets to them.
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('data-shopify-editor-section');
        wrap.appendChild(clone);

        resultsBox.appendChild(wrap);
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function () { runSearch(searchInput.value); });
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
        runSearch('');
      });
    }

    // ---- Nav clicks ----
    navItems.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        if (searchInput) searchInput.value = '';
        if (searchClear) searchClear.setAttribute('hidden', '');
        exitSearch();
        showPane(i);
      });
    });

    // ---- Activate a pane by handle (nav + hash) ----
    function activateByHandle(handle) {
      var targetIndex = -1;
      panes.forEach(function (pane, i) {
        if (pane.getAttribute('data-faq-category-pane') === handle) targetIndex = i;
      });
      if (targetIndex >= 0) {
        exitSearch();
        showPane(targetIndex);
        return true;
      }
      return false;
    }

    // ---- Hash handling: #category or #question-anchor ----
    function handleHash() {
      var raw = (window.location.hash || '').replace(/^#/, '');
      if (!raw) return false;

      if (activateByHandle(raw)) {
        var catPane = document.getElementById(raw);
        if (catPane && catPane.scrollIntoView) {
          catPane.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return true;
      }

      var item = document.getElementById(raw);
      if (item && item.matches && item.matches('[data-faq-item]')) {
        var parentPane = item.closest('[data-faq-category-pane]');
        if (parentPane) {
          activateByHandle(parentPane.getAttribute('data-faq-category-pane'));
        }
        item.setAttribute('open', '');
        if (item.scrollIntoView) {
          item.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return true;
      }
      return false;
    }

    root.__faqActivateByHandle = activateByHandle;
    root.__faqHandleHash = handleHash;

    // ---- Initial state ----
    if (!handleHash()) showPane(activeIndex);
  }

  function initAll() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      try {
        init(root);
      } catch (e) {
        if (window && window.console) console.warn('FAQ init failed:', e);
      }
    });
  }

  // ---- Boot ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ---- Global hashchange (bound once) ----
  window.addEventListener('hashchange', function () {
    var r = document.querySelector(ROOT_SELECTOR);
    if (r && typeof r.__faqHandleHash === 'function') r.__faqHandleHash();
  });

  // ---- Rail fade refresh ----
  // Resize, window load, and font settle can each change pill widths and
  // therefore whether the rail overflows; refresh the fade state on all
  // three. Panel height needs no refresh: its floor is the sidebar column,
  // handled entirely by the CSS grid.
  function refreshFaqLayout() {
    var r = document.querySelector(ROOT_SELECTOR);
    if (!r) return;
    if (typeof r.__faqNavFade === 'function') r.__faqNavFade();
  }
  var faqResizeTimer = null;
  window.addEventListener('resize', function () {
    if (faqResizeTimer) clearTimeout(faqResizeTimer);
    faqResizeTimer = setTimeout(refreshFaqLayout, 150);
  });
  window.addEventListener('load', refreshFaqLayout);

  // Webfonts swapping in after first paint change text metrics, which can
  // change whether the pill rail overflows; refresh once fonts settle, which
  // usually fires earlier than window load.
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(refreshFaqLayout);
  }

  // ---- Theme editor reinit ----
  // On add/edit/reorder/hide, Shopify re-renders sections without a full
  // reload. Because we never move wrappers, a re-rendered category section
  // simply reappears as a sibling in place. We reset roots and rebuild so the
  // sidebar/index pick up the change. Nothing is relocated.
  function resetAndRebuild() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (r) {
      r.__faqInit = false;
      r.removeAttribute('data-faq-enhanced');
      var nav = r.querySelector('[data-faq-nav]');
      if (nav) {
        Array.prototype.forEach.call(
          nav.querySelectorAll('.faq-nav__item'),
          function (b) { b.remove(); }
        );
      }
    });
    initAll();
  }

  document.addEventListener('shopify:section:load', resetAndRebuild);
  document.addEventListener('shopify:section:unload', resetAndRebuild);
  document.addEventListener('shopify:section:reorder', resetAndRebuild);

  // Reveal the pane for a selected category section.
  document.addEventListener('shopify:section:select', function (e) {
    var sec = e.target;
    if (!sec) return;
    var pane = (sec.matches && sec.matches('[data-faq-category-pane]'))
      ? sec
      : (sec.querySelector && sec.querySelector('[data-faq-category-pane]'));
    if (pane) activatePaneByNode(pane);
  });

  // Reveal the pane containing a selected block and open that question.
  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block) return;
    var pane = block.closest && block.closest('[data-faq-category-pane]');
    if (!pane) return;
    activatePaneByNode(pane);
    var item = (block.matches && block.matches('[data-faq-item]'))
      ? block
      : (block.querySelector && block.querySelector('[data-faq-item]'));
    if (item) item.setAttribute('open', '');
  });

  function activatePaneByNode(pane) {
    var root = document.querySelector(ROOT_SELECTOR);
    if (root && !root.__faqInit) initAll();
    var panes = getPanes();
    var idx = panes.indexOf(pane);
    if (idx < 0) return;
    panes.forEach(function (p, i) {
      var hide = i !== idx;
      if (hide && !paneIsHidden(p)) resetPaneToDefault(p);
      setPaneHidden(p, hide);
    });
    var navBtns = root ? root.querySelectorAll('.faq-nav__item') : [];
    Array.prototype.forEach.call(navBtns, function (b, i) {
      b.setAttribute('aria-current', i === idx ? 'true' : 'false');
    });
  }
})();